import styled from 'styled-components';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import UploadModal from '../molecules/UploadModal';

interface SidebarContainerProps {
  $isExpanded: boolean;
}

const SidebarContainer = styled.aside<SidebarContainerProps>`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  background-color: #1C0838;
  width: ${({ theme, $isExpanded }) =>
    $isExpanded ? theme.sizes.sidebar.expanded : theme.sizes.sidebar.collapsed};
  transition: width 0.3s ease;
  z-index: ${({ theme }) => theme.zIndex.sidebar};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

interface LogoContainerProps {
  $isExpanded: boolean;
}

const LogoContainer = styled.div<LogoContainerProps>`
  height: ${({ theme }) => theme.sizes.header.height};
  display: flex;
  align-items: center;
  padding: 0 ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  
  img.logo {
    height: 32px;
    opacity: ${({ $isExpanded }) => ($isExpanded ? 1 : 0)};
    transition: opacity 0.3s ease;
    margin-left: 40px;
  }
`;

const ToggleButton = styled.button`
  position: absolute;
  left: ${({ theme }) => theme.spacing.lg};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 24px;
    height: 24px;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.1);
  }
`;

const NavContainer = styled.nav`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.xl} 0;
  flex: 1;
`;

interface NavItemProps {
  $active?: boolean;
  $isExpanded: boolean;
  to: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

const StyledNavItem = styled(Link)<Omit<NavItemProps, 'children'>>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.text.primary : theme.colors.text.secondary};
  text-decoration: none;
  border-radius: 8px;
  background-color: ${({ theme, $active }) =>
    $active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  transition: all 0.2s ease;
  justify-content: ${({ $isExpanded }) => ($isExpanded ? 'flex-start' : 'center')};

  img {
    width: 24px;
    height: 24px;
    opacity: ${({ $active }) => ($active ? 1 : 0.7)};
    transition: opacity 0.2s ease;
  }

  span {
    opacity: ${({ $isExpanded }) => ($isExpanded ? 1 : 0)};
    display: ${({ $isExpanded }) => ($isExpanded ? 'block' : 'none')};
    transition: opacity 0.3s ease;
    white-space: nowrap;
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
    color: ${({ theme }) => theme.colors.text.primary};
    
    img {
      opacity: 1;
    }
  }
`;

const NavItemComponent: React.FC<Omit<NavItemProps, '$active' | '$isExpanded'> & { active?: boolean, isExpanded: boolean }> = ({ active, isExpanded, to, children, style }) => (
  <StyledNavItem to={to} $active={active} $isExpanded={isExpanded} style={style}>
    {children}
  </StyledNavItem>
);

interface UploadButtonProps {
  $isExpanded: boolean;
  children: React.ReactNode;
  onClick?: () => void;
}

const StyledUploadButton = styled.button<Omit<UploadButtonProps, 'children' | 'onClick'>>`
  margin: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.primary};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  transition: all 0.2s ease;
  width: ${({ $isExpanded }) => ($isExpanded ? 'auto' : '40px')};
  height: 40px;

  img {
    width: 24px;
    height: 24px;
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const UploadButtonComponent: React.FC<Omit<UploadButtonProps, '$isExpanded'> & { isExpanded: boolean }> = ({ isExpanded, children, onClick }) => (
  <StyledUploadButton $isExpanded={isExpanded} onClick={onClick}>
    {children}
  </StyledUploadButton>
);

interface SidebarProps {
  onToggle?: (isExpanded: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onToggle }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const location = useLocation();

  const toggleSidebar = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    onToggle?.(newState);
  };
  
  const handleOpenUploadModal = () => {
    setIsUploadModalOpen(true);
  };
  
  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
  };

  return (
    <SidebarContainer $isExpanded={isExpanded}>
      <LogoContainer $isExpanded={isExpanded}>
        <ToggleButton onClick={toggleSidebar}>
          <img src="/nav-icons/nav-icon.svg" alt="Toggle Menu" />
        </ToggleButton>
        <img className="logo" src="/creator-logo.svg" alt="BlinkLink Creator" />
      </LogoContainer>
      <NavContainer>
        <NavItemComponent 
          to="/" 
          active={location.pathname === '/'} 
          isExpanded={isExpanded}
        >
          <img src="/nav-icons/home-icon.svg" alt="Home" />
          <span>Home</span>
        </NavItemComponent>

        <NavItemComponent 
          to="/content" 
          active={location.pathname === '/content'} 
          isExpanded={isExpanded}
        >
          <img src="/nav-icons/content-icon.svg" alt="Content" />
          <span>Content</span>
        </NavItemComponent>
        <NavItemComponent 
          to="/wallet" 
          active={location.pathname === '/wallet'} 
          isExpanded={isExpanded}
        >
          <img src="/nav-icons/wallet-icon.svg" alt="Wallet" />
          <span>Wallet</span>
        </NavItemComponent>
        <NavItemComponent 
          to="/profile" 
          active={location.pathname === '/profile'} 
          isExpanded={isExpanded}
        >
          <img src="/nav-icons/profile-icon.svg" alt="Profile" />
          <span>Profile</span>
        </NavItemComponent>
        <UploadButtonComponent 
          isExpanded={isExpanded}
          onClick={handleOpenUploadModal}
        >
          <img src="/nav-icons/add-circle.svg" alt="Upload" />
          {isExpanded && <span>Upload</span>}
        </UploadButtonComponent>
      </NavContainer>

      <NavItemComponent 
        to="/settings" 
        active={location.pathname === '/settings'}
        isExpanded={isExpanded} 
        style={{ marginTop: 'auto', marginBottom: '16px' }}
      >
        <img src="/nav-icons/setting.svg" alt="Settings" />
        <span>Settings</span>
      </NavItemComponent>
      
      <UploadModal 
        isOpen={isUploadModalOpen} 
        onClose={handleCloseUploadModal} 
      />
    </SidebarContainer>
  );
};

export default Sidebar;
